import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import useRightBudgetAndImpressionsCalculation from '../../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'

import { createLineItem } from '../../../../../../modules/actions/lineItems'
import { setRightUploadAdData } from '../../../../../../modules/actions/rights'
import { facebookRightUploadAdCampaignSelector, rightSelector } from '../../../../../../modules/selectors/rights'
import { lineItemsSelector } from '../../../../../../modules/selectors/lineItems'
import { choicesPixelsSelector, facebookClientAppsSelector } from '../../../../../../modules/selectors/choices'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { transformValuesToBE } from '../formatters'
import { getInitialValues, getValidationSchema } from '../fields'
import { LINE_ITEM } from '../../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'
import { DIGITAL_ALLOCATION_TYPE_IMPRESSIONS } from '../../../../../Multiplatform/Partnerships/RightForms/fields'
import { COST_CAP, OUTCOME_APP_PROMOTION, OUTCOME_SALES, OUTCOME_TRAFFIC } from '../../../../../../constants/campaigns'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { BIDDING_TYPE } from '../../../../LineItemForms/LineItemFacebookCreate/LineItemFacebookCreateForm/fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../../hooks/formHooks/listItemFormHooks/fields'

const useHandleFormik = ({
  preselectedValues,
  selectedCampaignObjective,
  showPromotedObjectStepForAppPromotionCampaign,
  setSuccessSubmit
}) => {
  const dispatch = useDispatch()

  const right = useSelector(rightSelector)
  const pixels = useSelector(choicesPixelsSelector)
  // line items are loaded when submitting previous CampaignForm with existing campaign option
  const campaign = useSelector(facebookRightUploadAdCampaignSelector)
  const lineItems = useSelector(lineItemsSelector)
  const { currency_symbol: currencySymbol } = useSelector(selectedFacebookAdAccountSelector)
  const { id: facebookAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const facebookClientApps = useSelector(facebookClientAppsSelector)

  const { allocation_type: allocationTypeOnDigitalRight } = right
  const { objective: campaignObjective } = campaign

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const isOutcomeSalesCampaign = campaign.objective === OUTCOME_SALES
  const isOutcomeTrafficCampaign = campaign.objective === OUTCOME_TRAFFIC
  const isOutcomeAppPromotionCampaign = campaign.objective === OUTCOME_APP_PROMOTION
  const isCampaignBiddingTypeCostCap = campaign[BIDDING_TYPE] === COST_CAP
  const showImpressions = allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const showTargetCostCapField = useMemo(
    () =>
      (isOutcomeSalesCampaign || isOutcomeTrafficCampaign || isOutcomeAppPromotionCampaign) &&
      isCampaignBiddingTypeCostCap,
    [isOutcomeSalesCampaign, isOutcomeTrafficCampaign, isCampaignBiddingTypeCostCap, isOutcomeAppPromotionCampaign]
  )

  const handleExistedLineItemSubmit = useCallback(
    selectedLineItemId => {
      const selectedLineItemFullData = lineItems.find(lineItem => selectedLineItemId === lineItem.id)

      dispatch(setRightUploadAdData({ lineItem: selectedLineItemFullData }, FACEBOOK_PLATFORM))

      setSuccessSubmit(true)
    },
    [dispatch, lineItems, setSuccessSubmit]
  )

  const handleNewLineItemSubmit = useCallback(
    values => {
      const transformedData = transformValuesToBE({
        values,
        adAccountId: facebookAdAccountId,
        right,
        campaign,
        selectedCampaignObjective,
        campaignObjective,
        pixels,
        showTargetCostCapField,
        showPromotedObjectStepForAppPromotionCampaign,
        facebookClientApps
      })

      dispatch(createLineItem(transformedData, FACEBOOK_PLATFORM))
    },
    [
      campaign,
      campaignObjective,
      dispatch,
      facebookAdAccountId,
      pixels,
      right,
      selectedCampaignObjective,
      showTargetCostCapField,
      showPromotedObjectStepForAppPromotionCampaign,
      facebookClientApps
    ]
  )

  const onSubmit = useCallback(
    values => {
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        handleExistedLineItemSubmit(values[LINE_ITEM])
      } else {
        handleNewLineItemSubmit(values)
      }
    },
    [handleExistedLineItemSubmit, handleNewLineItemSubmit]
  )

  const isLineItemsExist = !!lineItems.length
  const initialValues = useMemo(
    () => getInitialValues({ isLineItemsExist, campaignObjective, preselectedValues }),
    [campaignObjective, isLineItemsExist, preselectedValues]
  )
  const validationSchema = useMemo(
    () =>
      getValidationSchema({
        campaign,
        showImpressions,
        remainingBudget,
        remainingImpressions,
        currencySymbol,
        showTargetCostCapField,
        showPromotedObjectStepForAppPromotionCampaign
      }),
    [
      campaign,
      showImpressions,
      remainingBudget,
      remainingImpressions,
      currencySymbol,
      showTargetCostCapField,
      showPromotedObjectStepForAppPromotionCampaign
    ]
  )
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })
  return usePurifiedFormik(formik)
}

export default useHandleFormik
